import { api } from "@/common/http/axios";

// ===========================================================
//  Passgate（飞书）登录路径——保留兼容
// ===========================================================

export const loginCallback = async (state: string) => {
  const res = await api.get("/auth/callback", { params: { state } });
  return res.data;
};

/** 由仿真后端拼接 Passgate 飞书登录 URL（与 brood-passgate ``PassgateClient.build_login_url`` 同源配置） */
export const getPassgateLoginUrl = async (originUrl: string): Promise<string | undefined> => {
  const res = await api.get("/auth/passgate/login-url", { params: { originUrl } });
  return res.data?.data?.loginUrl;
};

/** @deprecated 旧接口，仅作回滚兜底；新代码请使用 ``fetchMe()``。 */
export interface UserInfo {
  id: number;
  open_id: string;
  name: string;
  email: string;
  avatar_url: string;
  role: string;
}

export const fetchCurrentUser = async (): Promise<UserInfo | null> => {
  try {
    const res = await api.get("/auth/passgate/me");
    return (res.data?.data as UserInfo) ?? null;
  } catch {
    return null;
  }
};

export const fetchPassgateUserinfo = async (): Promise<unknown> => {
  const res = await api.get("/auth/passgate/userinfo");
  return res.data?.data;
};

export const refreshPassgateTokens = async (refreshToken: string): Promise<unknown> => {
  const res = await api.post("/auth/passgate/refresh", { refreshToken });
  return res.data?.data;
};

export const logoutPassgate = async (): Promise<void> => {
  try {
    await api.post("/auth/passgate/logout");
  } catch {
    // 忽略，前端会清 localStorage
  }
};


// ===========================================================
//  本地账号密码登录 + 统一 /auth/me（PR3 新增）
// ===========================================================

export interface MeUserVo {
  id: number;
  email: string;
  name: string;
  username?: string | null;
  avatarUrl?: string | null;
  source: string;
  status: number;
}

export interface BindingVo {
  bound: boolean;
  username?: string | null;
  email?: string | null;
  boundAt?: string | null;
}

export interface MeResponse {
  user: MeUserVo;
  bindings: { local: BindingVo; feishu: BindingVo };
  roles: string[];
  permissions: string[];
}

export interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: MeUserVo;
  bindings: { local: BindingVo; feishu: BindingVo };
  roles: string[];
  permissions: string[];
}

/** 账号密码登录：username 也可填邮箱，由后端归一化匹配。 */
export const loginByPassword = async (
  username: string,
  password: string,
): Promise<TokenResponse | null> => {
  const res = await api.post("/auth/login", { username, password });
  return (res.data?.data as TokenResponse) ?? null;
};

/** 用本地 refresh_token 换发新对（单次旋转）。 */
export const refreshLocal = async (
  refreshToken: string,
): Promise<TokenResponse | null> => {
  const res = await api.post("/auth/refresh", { refreshToken });
  return (res.data?.data as TokenResponse) ?? null;
};

/** 拉取当前登录用户的完整画像（用于路由守卫 / 菜单 / 按钮 v-permission）。 */
export const fetchMe = async (): Promise<MeResponse | null> => {
  try {
    const res = await api.get("/auth/me");
    return (res.data?.data as MeResponse) ?? null;
  } catch {
    return null;
  }
};

/** 修改当前用户密码（需带 access_token；改完老 access 立即失效）。 */
export const changePassword = async (
  oldPassword: string,
  newPassword: string,
): Promise<boolean> => {
  const res = await api.post("/auth/change-password", { oldPassword, newPassword });
  return res.data?.code === 0;
};

/** 本地登出：撤销 refresh family。后端不强制要求带 token。 */
export const logoutLocal = async (refreshToken?: string | null): Promise<void> => {
  try {
    await api.post("/auth/logout", refreshToken ? { refreshToken } : {});
  } catch {
    // 忽略
  }
};
